import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import salesRadarService from '../../services/salesRadarService';

// Circular Progress Gauge Component
const ProbabilityGauge = ({ value, size = 120, label = "Conversion" }) => {
  const getColor = (val) => {
    if (val >= 70) return '#22c55e';
    if (val >= 50) return '#f59e0b';
    if (val >= 30) return '#f97316';
    return '#ef4444';
  };

  const color = getColor(value);
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--gray-200)"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{ fontSize: '1.5rem', fontWeight: '700', color }}>{value}%</span>
        <span style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>{label}</span>
      </div>
    </div>
  );
};

// Key Factors Chart Component
const KeyFactorsChart = ({ factors }) => {
  const data = factors.map(f => ({
    name: f.name,
    impact: f.impact,
    fill: f.impact >= 0 ? '#22c55e' : '#ef4444'
  }));

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} layout="vertical" margin={{ left: 100 }}>
        <XAxis type="number" domain={[-50, 50]} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
        <RechartsTooltip formatter={(value) => [`${value > 0 ? '+' : ''}${value}%`, 'Impact']} />
        <Bar dataKey="impact" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={index} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// Mock data for leads with insights
const MOCK_LEADS_WITH_INSIGHTS = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@acme.com',
    company: 'Acme Corp',
    score: 85,
    conversion_probability: 78,
    probability_trend: 5,
    confidence_level: 88,
    key_factors: [
      { name: 'Pricing page visits', impact: 25 },
      { name: 'Demo requested', impact: 20 },
      { name: 'High engagement', impact: 15 },
      { name: 'Enterprise company', impact: 10 },
      { name: 'No phone provided', impact: -8 }
    ],
    actions: [
      { id: 1, type: 'meeting', label: 'Schedule Demo', priority: 'high', reason: 'Demo requested 2 days ago', expected_impact: 20 },
      { id: 2, type: 'email', label: 'Send Pricing', priority: 'high', reason: 'Visited pricing 5 times', expected_impact: 15 }
    ],
    ai_summary: 'Strong buying signals - prioritize demo scheduling. High engagement with pricing indicates budget awareness.',
    updated_at: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@techstart.io',
    company: 'TechStart',
    score: 72,
    conversion_probability: 65,
    probability_trend: 12,
    confidence_level: 75,
    key_factors: [
      { name: 'Multiple page views', impact: 18 },
      { name: 'Blog engagement', impact: 12 },
      { name: 'Email clicks', impact: 10 },
      { name: 'Small company', impact: -15 },
      { name: 'No demo yet', impact: -12 }
    ],
    actions: [
      { id: 1, type: 'content', label: 'Share Case Study', priority: 'medium', reason: 'Similar industry success story', expected_impact: 12 },
      { id: 2, type: 'email', label: 'Nurture Email', priority: 'medium', reason: 'Engaged but not converting', expected_impact: 8 }
    ],
    ai_summary: 'Growing interest - needs nurturing. Consider SMB-focused content to address company size concerns.',
    updated_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 3,
    name: 'Michael Chen',
    email: 'mchen@enterprise.com',
    company: 'Enterprise Inc',
    score: 92,
    conversion_probability: 88,
    probability_trend: 3,
    confidence_level: 92,
    key_factors: [
      { name: 'Demo completed', impact: 30 },
      { name: 'Proposal requested', impact: 25 },
      { name: 'Multiple stakeholders', impact: 15 },
      { name: 'Enterprise size', impact: 12 },
      { name: 'Long sales cycle', impact: -5 }
    ],
    actions: [
      { id: 1, type: 'call', label: 'Follow Up Call', priority: 'high', reason: 'Proposal sent 3 days ago', expected_impact: 25 },
      { id: 2, type: 'meeting', label: 'Stakeholder Meeting', priority: 'high', reason: 'Multiple decision makers involved', expected_impact: 20 }
    ],
    ai_summary: 'Hot opportunity - close to decision. Focus on stakeholder alignment and addressing any remaining concerns.',
    updated_at: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 4,
    name: 'Emily Davis',
    email: 'emily@startup.co',
    company: 'Startup Co',
    score: 45,
    conversion_probability: 32,
    probability_trend: -8,
    confidence_level: 60,
    key_factors: [
      { name: 'Initial interest', impact: 10 },
      { name: 'Webinar attended', impact: 8 },
      { name: 'No recent activity', impact: -20 },
      { name: 'Budget concerns', impact: -18 },
      { name: 'Early stage company', impact: -12 }
    ],
    actions: [
      { id: 1, type: 'email', label: 'Re-engagement Email', priority: 'low', reason: 'No activity in 14 days', expected_impact: 5 },
      { id: 2, type: 'content', label: 'ROI Calculator', priority: 'low', reason: 'Address budget concerns', expected_impact: 8 }
    ],
    ai_summary: 'Cooling lead - consider re-engagement campaign or moving to nurture track.',
    updated_at: new Date(Date.now() - 86400000).toISOString()
  }
];

function AIInsightsPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leadsWithInsights, setLeadsWithInsights] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [executingAction, setExecutingAction] = useState(null);

  const fetchLeadsWithInsights = useCallback(async () => {
    setLoading(true);
    try {
      const response = await salesRadarService.getLeadsWithInsights();
      if (response.success && response.data?.length > 0) {
        setLeadsWithInsights(response.data);
        setSelectedLead(response.data[0]);
      } else {
        setLeadsWithInsights(MOCK_LEADS_WITH_INSIGHTS);
        setSelectedLead(MOCK_LEADS_WITH_INSIGHTS[0]);
      }
    } catch (err) {
      console.error('Error fetching leads with insights:', err);
      setLeadsWithInsights(MOCK_LEADS_WITH_INSIGHTS);
      setSelectedLead(MOCK_LEADS_WITH_INSIGHTS[0]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeadsWithInsights();
  }, [fetchLeadsWithInsights]);

  const handleExecuteAction = async (action) => {
    setExecutingAction(action.id);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setExecutingAction(null);
    alert(`Action "${action.label}" executed successfully!`);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--green-600)';
    if (score >= 60) return 'var(--yellow-600)';
    if (score >= 40) return 'var(--orange-600)';
    return 'var(--red-600)';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: { bg: 'var(--red-100)', color: 'var(--red-700)', border: 'var(--red-200)' },
      medium: { bg: 'var(--yellow-100)', color: 'var(--yellow-700)', border: 'var(--yellow-200)' },
      low: { bg: 'var(--gray-100)', color: 'var(--gray-600)', border: 'var(--gray-200)' }
    };
    const style = styles[priority] || styles.low;
    return (
      <span style={{
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: '500',
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        textTransform: 'uppercase'
      }}>
        {priority}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{ marginBottom: '1rem' }}>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link">
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>AI Insights</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🧠 AI Insights
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              AI-powered lead analysis and recommendations
            </p>
          </div>
          <button className="btn btn-secondary" onClick={fetchLeadsWithInsights}>
            🔄 Refresh
          </button>
        </div>

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
          {/* Lead List */}
          <div style={{ borderRight: '1px solid var(--gray-200)', paddingRight: '1.5rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '1rem' }}>
              Leads with Insights ({leadsWithInsights.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {leadsWithInsights.map(lead => (
                <div
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: selectedLead?.id === lead.id ? '2px solid var(--primary-600)' : '1px solid var(--gray-200)',
                    background: selectedLead?.id === lead.id ? 'var(--red-50)' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--gray-900)' }}>{lead.name}</span>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: getScoreColor(lead.conversion_probability)
                    }}>
                      {lead.conversion_probability}%
                    </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: 0 }}>{lead.company}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                    <span style={{ fontSize: '0.7rem', color: lead.probability_trend > 0 ? 'var(--green-600)' : 'var(--red-600)' }}>
                      {lead.probability_trend > 0 ? '↑' : '↓'} {Math.abs(lead.probability_trend)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Lead Details */}
          {selectedLead && (
            <div>
              {/* Lead Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0 0 0.25rem 0', color: 'var(--gray-900)' }}>
                    {selectedLead.name}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>
                    {selectedLead.email} • {selectedLead.company}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', margin: '0.25rem 0 0 0' }}>
                    Updated: {new Date(selectedLead.updated_at).toLocaleString()}
                  </p>
                </div>
                <ProbabilityGauge value={selectedLead.conversion_probability} />
              </div>

              {/* AI Summary */}
              <div style={{
                background: 'var(--blue-50)',
                border: '1px solid var(--blue-200)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>💡</span>
                  <div>
                    <strong style={{ color: 'var(--blue-800)' }}>AI Summary:</strong>
                    <p style={{ margin: '0.25rem 0 0 0', color: 'var(--blue-700)', fontSize: '0.875rem' }}>
                      {selectedLead.ai_summary}
                    </p>
                  </div>
                </div>
              </div>

              {/* Two Column: Actions & Factors */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Recommended Actions */}
                <div>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem' }}>
                    📋 Recommended Actions
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {selectedLead.actions.map(action => (
                      <div
                        key={action.id}
                        style={{
                          padding: '0.75rem',
                          background: 'var(--gray-50)',
                          borderRadius: '8px',
                          border: '1px solid var(--gray-200)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {action.type === 'email' && '📧'}
                            {action.type === 'call' && '📞'}
                            {action.type === 'meeting' && '📅'}
                            {action.type === 'content' && '📄'}
                            {' '}{action.label}
                          </span>
                          {getPriorityBadge(action.priority)}
                        </div>
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', margin: '0 0 0.5rem 0' }}>
                          {action.reason}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            fontSize: '0.7rem',
                            color: 'var(--green-600)',
                            background: 'var(--green-100)',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px'
                          }}>
                            +{action.expected_impact}% impact
                          </span>
                          <button
                            onClick={() => handleExecuteAction(action)}
                            disabled={executingAction === action.id}
                            className="btn btn-primary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                          >
                            {executingAction === action.id ? '...' : 'Execute'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Factors */}
                <div>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.75rem' }}>
                    📊 Key Scoring Factors
                  </h4>
                  <KeyFactorsChart factors={selectedLead.key_factors} />
                  
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--green-600)' }}>✓ Positive</span>
                      {selectedLead.key_factors.filter(f => f.impact > 0).map((f, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', padding: '0.25rem 0' }}>
                          <span style={{ color: 'var(--gray-600)' }}>{f.name}</span>
                          <span style={{ color: 'var(--green-600)' }}>+{f.impact}%</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--red-600)' }}>⚠ Areas to Improve</span>
                      {selectedLead.key_factors.filter(f => f.impact < 0).map((f, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', padding: '0.25rem 0' }}>
                          <span style={{ color: 'var(--gray-600)' }}>{f.name}</span>
                          <span style={{ color: 'var(--red-600)' }}>{f.impact}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confidence & Trend */}
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '2rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Confidence Level</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <div style={{ width: '100px', height: '6px', background: 'var(--gray-200)', borderRadius: '9999px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${selectedLead.confidence_level}%`, background: 'var(--primary-600)', borderRadius: '9999px' }}></div>
                    </div>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{selectedLead.confidence_level}%</span>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Probability Trend</span>
                  <div style={{ marginTop: '0.25rem' }}>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: selectedLead.probability_trend > 0 ? 'var(--green-600)' : 'var(--red-600)'
                    }}>
                      {selectedLead.probability_trend > 0 ? '↑' : '↓'} {Math.abs(selectedLead.probability_trend)}% vs last week
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Lead Score</span>
                  <div style={{ marginTop: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: getScoreColor(selectedLead.score) }}>
                      {selectedLead.score} / 100
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIInsightsPanel;
